/**
 * Settings > Payments (tracker item 38): connect the card processor (OAuth
 * redirect; the callback returns here with ?connect=success|error), pick the
 * location payments are taken at, disconnect (with confirmation), the
 * online payments master switch (only with a connected processor), the
 * smallest online payment and the autopay retry rules. ADMIN only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createAPIClient, IOnlinePaymentsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { connectResultMessage, parseRetryDays } from '../../features/online-payments/onlinePaymentsUi';
import { Toast } from '../../components/Toast';

interface SettingsForm {
  enabled: boolean;
  minAmount: string;
  retryDays: string;
  maxFailures: string;
}

const toForm = (s: types.OnlinePaymentSettings): SettingsForm => ({
  enabled: s.enabled,
  minAmount: s.minAmount,
  retryDays: s.autopayRetryDays.join(', '),
  maxFailures: String(s.autopayMaxFailures),
});

/** Field problems of the settings form ({} = fine). */
export function validatePaymentsForm(form: SettingsForm): Partial<Record<keyof SettingsForm, string>> {
  const errors: Partial<Record<keyof SettingsForm, string>> = {};
  if (!/^\d+(\.\d{1,2})?$/.test(form.minAmount.trim()) || Number(form.minAmount) < 0.01) errors.minAmount = 'An amount of 0.01 or more';
  const days = parseRetryDays(form.retryDays);
  if (typeof days === 'string') errors.retryDays = days;
  if (!/^\d+$/.test(form.maxFailures.trim()) || Number(form.maxFailures) < 1 || Number(form.maxFailures) > 10) errors.maxFailures = 'A whole number from 1 to 10';
  return errors;
}

export function PaymentsSettingsPage() {
  const api = useMemo<IOnlinePaymentsApi>(() => createAPIClient(), []);
  const location = useLocation();
  const navigate = useNavigate();
  const [connection, setConnection] = useState<types.ProcessorConnection | null>(null);
  const [settings, setSettings] = useState<types.OnlinePaymentSettings | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [locationId, setLocationId] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; message: string } | null>(() => connectResultMessage(location.search));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [conn, s] = await Promise.all([api.getProcessorConnection(), api.getOnlinePaymentSettings()]);
      setConnection(conn);
      setLocationId(conn.locationId ?? '');
      setSettings(s);
      setForm(toForm(s));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the payment settings'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  // The OAuth callback flag is shown once; drop it from the address bar
  useEffect(() => {
    if (location.search.includes('connect=')) navigate('/settings/payments', { replace: true });
  }, [location.search, navigate]);

  const connected = connection?.status === 'CONNECTED';
  const usable = connected && !!connection?.locationId;
  const errors = form ? validatePaymentsForm(form) : {};

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.startProcessorConnect();
      if (url.startsWith('/')) {
        // A same-origin URL (the test processor): the callback result is local
        navigate(url);
        await load();
        setNotice(connectResultMessage(url.slice(url.indexOf('?'))));
      } else {
        window.location.assign(url);
      }
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not start the connection'));
    } finally {
      setBusy(false);
    }
  }

  async function saveLocation() {
    setBusy(true);
    setError(null);
    try {
      setConnection(await api.chooseProcessorLocation(locationId));
      setNotice({ ok: true, message: 'Location saved.' });
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save the location'));
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.disconnectProcessor();
      setConfirmDisconnect(false);
      setNotice({
        ok: true,
        message: result.revokedAtProcessor
          ? 'Disconnected. Online payments and every autopay are off.'
          : 'Disconnected here (the processor could not be reached to revoke access). Online payments and every autopay are off.',
      });
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not disconnect'));
    } finally {
      setBusy(false);
    }
  }

  async function saveSettings(event: React.FormEvent) {
    event.preventDefault();
    if (!form || Object.keys(errors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await api.updateOnlinePaymentSettings({
        enabled: form.enabled,
        minAmount: Number(form.minAmount).toFixed(2),
        autopayRetryDays: parseRetryDays(form.retryDays) as number[],
        autopayMaxFailures: Number(form.maxFailures),
      });
      setSettings(saved);
      setForm(toForm(saved));
      setNotice({ ok: true, message: saved.enabled ? 'Online payments are on.' : 'Payment settings saved.' });
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save the payment settings'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">Loading payment settings…</main>;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Payments (online and autopay)</h1>
      </div>

      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind={notice.ok ? 'success' : 'error'}>{notice.message}</Toast>}

      <section aria-labelledby="processor-heading" className="bg-white rounded-lg shadow p-5 space-y-4">
        <h2 id="processor-heading" className="font-semibold text-lg">Card processor</h2>
        {connection?.status === 'ERROR' && (
          <p role="note" className="rounded bg-yellow-50 p-3 text-sm text-yellow-900">
            The connection needs to be renewed{connection.lastError ? ` (${connection.lastError})` : ''}. Online and automatic payments are paused until you reconnect.
          </p>
        )}
        {connected && connection ? (
          <div className="space-y-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><dt className="text-gray-600">Processor</dt><dd className="font-medium">{connection.providerName ?? connection.provider}{connection.environment === 'SANDBOX' && <span className="ml-2 rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">Sandbox (test)</span>}</dd></div>
              <div><dt className="text-gray-600">Account</dt><dd className="font-medium break-words">{connection.accountName ?? connection.accountId}</dd></div>
              <div><dt className="text-gray-600">Location</dt><dd className="font-medium">{connection.locationName ?? <span className="text-amber-700">Not chosen</span>}</dd></div>
              {connection.accessTokenExpiresAt && (
                <div><dt className="text-gray-600">Access renews before</dt><dd>{connection.accessTokenExpiresAt.slice(0, 10)}</dd></div>
              )}
            </dl>
            {connection.locations.length > 1 && (
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[14rem] flex-1">
                  <label htmlFor="processor-location" className={labelClass}>Take payments at</label>
                  <select id="processor-location" className={inputClass} value={locationId} disabled={busy} onChange={(e) => setLocationId(e.target.value)}>
                    <option value="">Choose a location</option>
                    {connection.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <button type="button" className={secondaryButtonClass} disabled={busy || !locationId || locationId === connection.locationId} onClick={() => void saveLocation()}>
                  Save location
                </button>
              </div>
            )}
            {confirmDisconnect ? (
              <div role="alertdialog" aria-labelledby="disconnect-title" className="rounded border border-red-200 bg-red-50 p-4 space-y-3">
                <p id="disconnect-title" className="font-semibold text-red-800">Disconnect the card processor?</p>
                <p className="text-sm text-red-800">Online payments and every customer&apos;s autopay are turned off. Cards on file stay listed but cannot be charged until you reconnect the same account.</p>
                <div className="flex flex-wrap gap-3">
                  <button type="button" className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-gray-400 min-h-[44px] sm:min-h-0" disabled={busy} onClick={() => void disconnect()}>Disconnect</button>
                  <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirmDisconnect(false)}>Keep connected</button>
                </div>
              </div>
            ) : (
              <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirmDisconnect(true)}>Disconnect…</button>
            )}
          </div>
        ) : connection?.availableProvider ? (
          <div className="space-y-3 text-sm text-gray-700">
            <p>Connect your {connection.availableProvider.name} account so customers can pay invoices online. Payments go straight to your own account.</p>
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void connect()}>
              {connection.status === 'ERROR' ? 'Reconnect' : 'Connect'} {connection.availableProvider.name}
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-700">No card processor is set up on this server yet. Ask your administrator to configure one.</p>
        )}
      </section>

      {form && settings && (
        <form onSubmit={(e) => void saveSettings(e)} aria-labelledby="online-settings-heading" className="bg-white rounded-lg shadow p-5 space-y-4">
          <h2 id="online-settings-heading" className="font-semibold text-lg">Online payments</h2>
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1 h-5 w-5" checked={form.enabled} disabled={busy || (!usable && !form.enabled)}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })} aria-describedby="online-enabled-hint" />
            <span>
              <span className="font-medium">Let customers pay invoices online</span>
              <span id="online-enabled-hint" className="block text-xs text-gray-500">
                {usable ? 'Adds Pay now to emailed invoice links, and allows cards on file and autopay.' : 'Connect a card processor (and choose a location) first.'}
              </span>
            </span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="online-min" className={labelClass}>Smallest online payment ($)</label>
              <input id="online-min" inputMode="decimal" className={inputClass} value={form.minAmount} disabled={busy}
                onChange={(e) => setForm({ ...form, minAmount: e.target.value })} aria-invalid={!!errors.minAmount} />
              {errors.minAmount && <p className="mt-1 text-xs text-red-700">{errors.minAmount}</p>}
              <p className="mt-1 text-xs text-gray-500">A smaller balance can always be paid in full.</p>
            </div>
            <div>
              <label htmlFor="online-retry" className={labelClass}>Autopay retry days</label>
              <input id="online-retry" className={inputClass} value={form.retryDays} disabled={busy}
                onChange={(e) => setForm({ ...form, retryDays: e.target.value })} aria-invalid={!!errors.retryDays} />
              {errors.retryDays && <p className="mt-1 text-xs text-red-700">{errors.retryDays}</p>}
              <p className="mt-1 text-xs text-gray-500">Days after the first attempt, e.g. 0, 3, 7.</p>
            </div>
            <div>
              <label htmlFor="online-max-failures" className={labelClass}>Turn autopay off after (failed tries)</label>
              <input id="online-max-failures" inputMode="numeric" className={inputClass} value={form.maxFailures} disabled={busy}
                onChange={(e) => setForm({ ...form, maxFailures: e.target.value })} aria-invalid={!!errors.maxFailures} />
              {errors.maxFailures && <p className="mt-1 text-xs text-red-700">{errors.maxFailures}</p>}
            </div>
          </div>
          {!settings.autopayWorkerEnabled && (
            <p role="note" className="text-xs text-gray-600">Automatic charging is not running on this server yet: saved cards can be charged from the invoice page.</p>
          )}
          {settings.autopayWorkerEnabled && settings.autopayDryRun && (
            <p role="note" className="text-xs text-amber-700">Autopay is in dry-run mode on this server: it logs what it would charge and charges nothing.</p>
          )}
          <button type="submit" className={primaryButtonClass} disabled={busy || Object.keys(errors).length > 0}>Save</button>
        </form>
      )}
    </main>
  );
}
